import type { TEntry, IdentifierIntakeContextOutput } from '@schemas'
import { EntryEligibilityError, staffEntryRestriction } from '@/lib/entry-eligibility'
import { pendingEntryRequest } from './pending-entry-request'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ENTRY_INTAKE_INITIAL_STATE,
  type EntryIntakeState,
  type PendingIntakeEntry,
  type RecentIntakeEntry,
  type CheckedInExhibit,
  type CheckedInExhibitInput,
} from './entry-intake-state'
import { ENTRY_INTAKE_UPDATE_EVENT } from './entry-intake-events'
import { loadEntryIntakeState, mutateEntryIntakeState } from '@/data/entry-intake-store'
import { apiClient } from '@/lib/apiClient'
import {
  applyIntakeContext,
  compatibleIntakeCode,
  intakeContextCodes,
  recordIntakeAssignment,
} from './identifier-intake-context'

export type { PendingIntakeEntry, CheckedInExhibit, RecentIntakeEntry } from './entry-intake-state'

export function useEntryIntakeManager(showId?: string) {
  const resolvedShowId = useMemo(() => showId ?? null, [showId])
  const [state, setState] = useState<EntryIntakeState>(ENTRY_INTAKE_INITIAL_STATE)
  const initializedRef = useRef(false)
  const [persistenceError, setPersistenceError] = useState<string | null>(null)
  const ownerRef = useRef(crypto.randomUUID())
  const showIdRef = useRef<string | null>(null)

  const applyState = useCallback((targetShowId: string, next: EntryIntakeState) => {
    if (showIdRef.current !== targetShowId) return
    initializedRef.current = true
    setState(current => ((next.revision ?? 0) >= (current.revision ?? 0) ? next : current))
    setPersistenceError(null)
  }, [])

  useEffect(() => {
    let cancelled = false
    showIdRef.current = resolvedShowId
    initializedRef.current = !resolvedShowId
    setState(ENTRY_INTAKE_INITIAL_STATE)
    setPersistenceError(null)
    const hydrate = async () => {
      if (!resolvedShowId) return
      try {
        const next = await loadEntryIntakeState(resolvedShowId)
        if (!cancelled) applyState(resolvedShowId, next)
      } catch {
        if (!cancelled)
          setPersistenceError(
            'Unable to save intake on this device. Free storage or reopen the browser before adding entries.'
          )
      }
    }
    const refresh = (event: Event) => {
      if ((event as CustomEvent).detail?.showId === resolvedShowId) void hydrate()
    }
    void hydrate()
    window.addEventListener(ENTRY_INTAKE_UPDATE_EVENT, refresh)
    return () => {
      cancelled = true
      window.removeEventListener(ENTRY_INTAKE_UPDATE_EVENT, refresh)
    }
  }, [resolvedShowId, applyState])

  const commit = useCallback(
    async (updater: (prev: EntryIntakeState) => EntryIntakeState) => {
      const targetShowId = resolvedShowId
      if (!targetShowId) throw new Error('Select a show before saving intake.')
      try {
        const next = await mutateEntryIntakeState(targetShowId, updater)
        applyState(targetShowId, next)
        return next
      } catch (error) {
        if (!(error instanceof EntryEligibilityError))
          setPersistenceError(
            'Unable to save intake on this device. Your submission has not been safely queued.'
          )
        throw error
      }
    },
    [resolvedShowId, applyState]
  )

  const pushCodes = useCallback(
    (codes: string[], identifiers: { value: string; orderIndex: number }[] = []) => {
      if (!codes.length) return Promise.resolve()
      return commit(prev => {
        const existing = new Set(
          [...prev.availableCodes, ...(prev.retiredCodes ?? [])].map(code => code.toUpperCase())
        )
        const filtered = codes.filter(code => {
          const normalized = code.toUpperCase()
          if (existing.has(normalized)) return false
          existing.add(normalized)
          return true
        })
        return {
          ...prev,
          availableCodes: [...prev.availableCodes, ...filtered],
          identifierOrders: {
            ...prev.identifierOrders,
            ...Object.fromEntries(
              identifiers.map(identifier => [identifier.value, identifier.orderIndex])
            ),
          },
        }
      })
    },
    [commit]
  )

  const refreshAssignmentContext = useCallback(
    async (exhibitorId: string) => {
      const started = await commit(prev => {
        const previous = prev.assignmentContexts?.[exhibitorId]
        return {
          ...prev,
          assignmentContexts: {
            ...prev.assignmentContexts,
            [exhibitorId]: {
              generation: (previous?.generation ?? 0) + 1,
              isShowStaff: previous?.isShowStaff,
              // Only the generation moves: a request that fails or never
              // returns leaves the last snapshot in force.
              complete: previous?.complete ?? false,
              assignedOrderIndexes: previous?.assignedOrderIndexes ?? [],
              candidates: previous?.candidates ?? {},
            },
          },
        }
      })
      const boundary = {
        exhibitorId,
        generation: started.assignmentContexts![exhibitorId].generation,
        revision: started.revision!,
        codes: intakeContextCodes(started, exhibitorId),
      }
      const responses: IdentifierIntakeContextOutput[] = []
      // Legacy pools can exceed the API's bounded request size. Different chunks
      // must observe identical assignment history before the snapshot is trusted.
      for (let offset = 0; offset < Math.max(1, boundary.codes.length); offset += 500) {
        responses.push(
          await apiClient.identifierIntakeContext(resolvedShowId!, {
            exhibitorId,
            codes: boundary.codes.slice(offset, offset + 500),
          })
        )
      }
      const assignedOrderIndexes = responses[0].assignedOrderIndexes
      const history = JSON.stringify([...assignedOrderIndexes].sort((a, b) => a - b))
      const candidates = responses.flatMap(response => response.candidates)
      const response = {
        isShowStaff: responses.some(chunk => chunk.isShowStaff === true)
          ? true
          : responses.every(chunk => chunk.isShowStaff === false)
            ? false
            : undefined,
        assignedOrderIndexes,
        candidates,
        complete:
          responses.every(
            chunk =>
              chunk.complete &&
              JSON.stringify([...chunk.assignedOrderIndexes].sort((a, b) => a - b)) === history
          ) && boundary.codes.every(code => candidates.some(candidate => candidate.value === code)),
      }
      await commit(prev => applyIntakeContext(prev, boundary, response))
      // A newer request can fence this cache update; callers still need the
      // eligibility this request verified, rather than a retained older cache.
      return response
    },
    [commit, resolvedShowId]
  )

  const claim = useCallback((prev: EntryIntakeState, exhibitorId: string) => {
    const code = compatibleIntakeCode(prev, exhibitorId)
    if (code) {
      return {
        ...prev,
        availableCodes: prev.availableCodes.filter(value => value !== code),
        retiredCodes: [...(prev.retiredCodes ?? []), code],
        lastClaimedCanonical: code,
        lastClaimedOwner: ownerRef.current,
      }
    }
    return {
      ...prev,
      tmpCounter: prev.tmpCounter + 1,
      lastClaimedCanonical: null,
      lastClaimedOwner: ownerRef.current,
    }
  }, [])

  const claimCode = useCallback(
    async (exhibitorId: string): Promise<{ code: string; source: 'canonical' | 'temporary' }> => {
      const next = await commit(prev => claim(prev, exhibitorId))
      return next.lastClaimedCanonical
        ? { code: next.lastClaimedCanonical, source: 'canonical' }
        : { code: `TMP-${next.tmpCounter.toString().padStart(4, '0')}`, source: 'temporary' }
    },
    [commit, claim]
  )

  const claimAndAddPending = useCallback(
    async (
      draft: Omit<PendingIntakeEntry, 'code'>,
      { staffMayExhibit = true }: { staffMayExhibit?: boolean } = {}
    ) => {
      const next = await commit(prev => {
        const restriction = staffEntryRestriction(
          staffMayExhibit,
          prev.assignmentContexts?.[draft.exhibitor.id]?.isShowStaff
        )
        if (restriction) throw new EntryEligibilityError(restriction)
        const claimed = claim(prev, draft.exhibitor.id)
        const code =
          claimed.lastClaimedCanonical ?? `TMP-${claimed.tmpCounter.toString().padStart(4, '0')}`
        return {
          ...claimed,
          lastClaimedCanonical: null,
          lastClaimedOwner: null,
          pendingEntries: [...claimed.pendingEntries, { ...draft, code }],
        }
      })
      return next.pendingEntries.find(entry => entry.id === draft.id)!
    },
    [commit, claim]
  )

  const addPending = useCallback(
    (entry: PendingIntakeEntry) => {
      return commit(prev => ({
        ...prev,
        pendingEntries: prev.pendingEntries.some(existing => existing.id === entry.id)
          ? prev.pendingEntries
          : [...prev.pendingEntries, entry],
      }))
    },
    [commit]
  )

  const updatePending = useCallback(
    // `expected` makes the write conditional on what the caller last saw;
    // another tab may have changed the entry since. A refused write leaves
    // the entry as stored.
    (id: string, updates: Partial<PendingIntakeEntry>, expected?: { notes: string }) => {
      return commit(prev => ({
        ...prev,
        pendingEntries: prev.pendingEntries.map(entry =>
          entry.id === id && (!expected || (entry.notes ?? '') === expected.notes)
            ? {
                ...entry,
                ...updates,
                submissionId: entry.submissionId,
                completionIntent: entry.completionIntent,
                syncAttempt: entry.syncAttempt,
                editVersion:
                  (entry.editVersion ?? 0) +
                  (['notes', 'code', 'clubId', 'clubName'].some(key => key in updates) ? 1 : 0),
              }
            : entry
        ),
      }))
    },
    [commit]
  )

  // Staff try a duplicate again only while it is still waiting: another tab may already be syncing it.
  const retryDuplicate = useCallback(
    (id: string) =>
      commit(prev => ({
        ...prev,
        pendingEntries: prev.pendingEntries.map(entry =>
          entry.id === id && entry.status === 'duplicate' && !entry.syncAttempt
            ? { ...entry, status: 'queued' as const, error: undefined }
            : entry
        ),
      })),
    [commit]
  )

  const replaceRejectedCode = useCallback(
    async (id: string) =>
      commit(prev => {
        const entry = prev.pendingEntries.find(item => item.id === id)
        if (!entry?.canReplaceCode || entry.status !== 'error')
          throw new Error('This entry has no confirmed Exhibit ID rejection to resolve.')
        const counter = prev.tmpCounter + 1
        return {
          ...prev,
          tmpCounter: counter,
          pendingEntries: prev.pendingEntries.map(item =>
            item.id === id
              ? {
                  ...item,
                  code: `TMP-${counter.toString().padStart(4, '0')}`,
                  rejectedCode: item.code,
                  canReplaceCode: false,
                  status: 'queued' as const,
                  error: undefined,
                  editVersion: (item.editVersion ?? 0) + 1,
                }
              : item
          ),
        }
      }),
    [commit]
  )

  const preparePendingSync = useCallback(
    async (id: string, canStart = true) => {
      const next = await commit(prev => ({
        ...prev,
        pendingEntries: prev.pendingEntries.map(entry => {
          if (
            entry.id !== id ||
            (!canStart && !entry.syncAttempt) ||
            (entry.status === 'duplicate' && !entry.syncAttempt)
          )
            return entry
          // A new server-allocated code must not overtake this entrant's other
          // unsent claims: earlier entries keep their order, and a later entry
          // that still holds an Exhibit ID could end up beside the new one.
          // Existing frozen attempts remain replayable. A waiting entry is
          // returned untouched.
          const position = prev.pendingEntries.indexOf(entry)
          if (
            !entry.syncAttempt &&
            entry.code.startsWith('TMP') &&
            prev.pendingEntries.some(
              (other, index) =>
                other !== entry &&
                other.exhibitor.id === entry.exhibitor.id &&
                !other.syncAttempt?.createdEntry &&
                !other.syncAttempt?.completed &&
                (index < position || !(other.code.startsWith('TMP') || other.canReplaceCode))
            )
          )
            return entry
          return {
            ...entry,
            status: 'syncing' as const,
            syncAttempt: entry.syncAttempt ?? {
              id: crypto.randomUUID(),
              editVersion: entry.editVersion ?? 0,
              request: pendingEntryRequest(resolvedShowId!, entry),
              completionIntent: entry.completionIntent ?? 'create_only',
            },
          }
        }),
      }))
      return next.pendingEntries.find(entry => entry.id === id) ?? null
    },
    [commit, resolvedShowId]
  )

  const savePendingCreation = useCallback(
    async (id: string, attemptId: string, createdEntry: TEntry) => {
      const next = await commit(prev => {
        const pending = prev.pendingEntries.find(
          entry => entry.id === id && entry.syncAttempt?.id === attemptId
        )
        if (!pending) return prev
        return {
          ...recordIntakeAssignment(prev, createdEntry),
          pendingEntries: prev.pendingEntries.map(entry =>
            entry === pending
              ? { ...entry, syncAttempt: { ...entry.syncAttempt!, createdEntry } }
              : entry
          ),
        }
      })
      return (
        next.pendingEntries.find(entry => entry.id === id && entry.syncAttempt?.id === attemptId) ??
        null
      )
    },
    [commit]
  )

  const rejectPendingCreation = useCallback(
    (id: string, attemptId: string, updates: Partial<PendingIntakeEntry>) =>
      commit(prev => ({
        ...prev,
        pendingEntries: prev.pendingEntries.map(entry =>
          entry.id === id && entry.syncAttempt?.id === attemptId && !entry.syncAttempt.createdEntry
            ? { ...entry, ...updates, syncAttempt: undefined }
            : entry
        ),
      })),
    [commit]
  )

  const finishPendingSync = useCallback(
    async (id: string, editVersion: number, entryId?: string) => {
      const next = await commit(prev => ({
        ...prev,
        pendingEntries: prev.pendingEntries.flatMap(entry => {
          if (entry.id !== id) return [entry]
          if ((entry.editVersion ?? 0) === editVersion) return []
          return [
            {
              ...entry,
              status: 'error' as const,
              existingEntryId: entryId,
              syncAttempt: entry.syncAttempt
                ? { ...entry.syncAttempt, completed: true }
                : undefined,
              error:
                'Earlier details were saved while this entry was being edited. Review the saved entry before retrying.',
            },
          ]
        }),
      }))
      return !next.pendingEntries.some(entry => entry.id === id)
    },
    [commit]
  )

  const removePending = useCallback(
    (id: string) =>
      commit(prev => {
        const entry = prev.pendingEntries.find(item => item.id === id)
        if (entry?.syncAttempt && !entry.syncAttempt.completed) {
          throw new Error(
            'Resolve this pending submission before removing it; the server may already have saved it.'
          )
        }
        return { ...prev, pendingEntries: prev.pendingEntries.filter(entry => entry.id !== id) }
      }),
    [commit]
  )

  const clearAll = useCallback(
    () =>
      commit(prev => ({
        ...ENTRY_INTAKE_INITIAL_STATE,
        retiredCodes: prev.retiredCodes,
        tmpCounter: prev.tmpCounter,
        identifierOrders: prev.identifierOrders,
        assignmentContexts: prev.assignmentContexts,
        localAssignments: prev.localAssignments,
      })),
    [commit]
  )

  const recordRecentEntry = useCallback(
    (entry: RecentIntakeEntry) => {
      if (!entry.id || !entry.entryCode || !entry.exhibitorId) return
      return commit(prev => {
        const filtered = prev.recentEntries.filter(existing => existing.id !== entry.id)
        return {
          ...prev,
          recentEntries: [entry, ...filtered].slice(0, 10),
        }
      })
    },
    [commit]
  )

  const removeRecentEntry = useCallback(
    (entryId?: string | null, entryCode?: string | null) => {
      if (!entryId && !entryCode) return
      return commit(prev => ({
        ...prev,
        recentEntries: prev.recentEntries.filter(existing => {
          if (entryId && existing.id === entryId) return false
          if (entryCode && existing.entryCode === entryCode) return false
          return true
        }),
      }))
    },
    [commit]
  )

  const clearRecentEntries = useCallback(() => {
    return commit(prev => ({
      ...prev,
      recentEntries: [],
    }))
  }, [commit])

  const recordCheckedInExhibit = useCallback(
    (payload: CheckedInExhibitInput) => {
      if (!payload?.exhibitId || !payload.entryId || !payload.entryCode) return
      return commit(prev => {
        const existing = prev.checkedInExhibits.find(
          item => item.entryId === payload.entryId || item.exhibitId === payload.exhibitId
        )
        const recent =
          prev.recentEntries.find(
            entry => entry.id === payload.entryId || entry.entryCode === payload.entryCode
          ) ?? null

        const resolvedTimestamp =
          payload.checkedInAt && !Number.isNaN(Date.parse(payload.checkedInAt))
            ? payload.checkedInAt
            : (existing?.checkedInAt ?? new Date().toISOString())

        const resolvedClassId = payload.classId ?? existing?.classId ?? null

        const resolvedClassCode = payload.classCode ?? existing?.classCode ?? null

        const resolvedClassName =
          payload.className ?? existing?.className ?? recent?.className ?? null

        const resolvedExhibitorId =
          payload.exhibitorId ?? existing?.exhibitorId ?? recent?.exhibitorId ?? null

        const resolvedExhibitorName = payload.exhibitorName ?? existing?.exhibitorName ?? null

        const resolvedRemap =
          payload.remappedFrom ??
          existing?.remappedFrom ??
          (recent?.originalCode && recent.originalCode !== payload.entryCode
            ? recent.originalCode
            : null)

        const nextRecord: CheckedInExhibit = {
          exhibitId: payload.exhibitId,
          entryId: payload.entryId,
          entryCode: payload.entryCode,
          classId: resolvedClassId,
          classCode: resolvedClassCode,
          className: resolvedClassName,
          exhibitorId: resolvedExhibitorId,
          exhibitorName: resolvedExhibitorName,
          checkedInAt: resolvedTimestamp,
          remappedFrom: resolvedRemap,
        }

        const filtered = prev.checkedInExhibits.filter(
          item => item.exhibitId !== nextRecord.exhibitId && item.entryId !== nextRecord.entryId
        )
        const next = [...filtered, nextRecord]
        const limit = 50
        return {
          ...prev,
          checkedInExhibits: next.slice(-limit),
        }
      })
    },
    [commit]
  )

  const removeCheckedInExhibit = useCallback(
    (entryId?: string | null, exhibitId?: string | null) => {
      if (!entryId && !exhibitId) return
      return commit(prev => ({
        ...prev,
        checkedInExhibits: prev.checkedInExhibits.filter(item => {
          if (entryId && item.entryId === entryId) return false
          if (exhibitId && item.exhibitId === exhibitId) return false
          return true
        }),
      }))
    },
    [commit]
  )

  const returnLastClaimed = useCallback(
    () =>
      commit(prev => {
        const code = prev.lastClaimedCanonical
        if (
          !code ||
          prev.lastClaimedOwner !== ownerRef.current ||
          prev.pendingEntries.some(entry => entry.code === code)
        )
          return prev
        return {
          ...prev,
          availableCodes: [code, ...prev.availableCodes],
          retiredCodes: prev.retiredCodes?.filter(value => value !== code),
          lastClaimedCanonical: null,
          lastClaimedOwner: null,
        }
      }),
    [commit]
  )

  const clearLastClaimed = useCallback(
    () =>
      commit(prev =>
        prev.lastClaimedOwner === ownerRef.current
          ? { ...prev, lastClaimedCanonical: null, lastClaimedOwner: null }
          : prev
      ),
    [commit]
  )

  const returnCode = useCallback(
    (code: string) => {
      if (state.lastClaimedCanonical === code) return returnLastClaimed()
      return Promise.resolve()
    },
    [state.lastClaimedCanonical, returnLastClaimed]
  )

  return {
    initialized: initializedRef.current,
    persistenceError,
    availableCodes: state.availableCodes,
    assignmentContexts: state.assignmentContexts,
    pendingEntries: state.pendingEntries,
    recentEntries: state.recentEntries,
    lastClaimedCode:
      state.lastClaimedOwner === ownerRef.current ? state.lastClaimedCanonical : null,
    checkedInExhibits: state.checkedInExhibits,
    pushCodes,
    refreshAssignmentContext,
    claimCode,
    claimAndAddPending,
    returnCode,
    addPending,
    updatePending,
    removePending,
    preparePendingSync,
    savePendingCreation,
    rejectPendingCreation,
    retryDuplicate,
    replaceRejectedCode,
    finishPendingSync,
    clearAll,
    recordRecentEntry,
    removeRecentEntry,
    clearRecentEntries,
    recordCheckedInExhibit,
    removeCheckedInExhibit,
    returnLastClaimed,
    clearLastClaimed,
  }
}
